import React, { useState, useContext, useEffect, useMemo } from 'react';
import { AuthContext } from './AuthContext';

const buildFormState = (currentUser) => ({
  firstName: currentUser?.first_name || '',
  lastName: currentUser?.last_name || '',
  title: currentUser?.title || '',
  bio: currentUser?.bio || '',
  language: currentUser?.language || 'Español',
  social: {
    facebook: currentUser?.social?.facebook || '',
    instagram: currentUser?.social?.instagram || '',
    linkedin: currentUser?.social?.linkedin || '',
    tiktok: currentUser?.social?.tiktok || ''
  }
});

const buildSettingsState = (currentUser) => ({
  showProfile: currentUser?.settings?.showProfile ?? true,
  showCourses: currentUser?.settings?.showCourses ?? true
});

const StudentProfile = ({ embedded = false, defaultTab = 'perfil' }) => {
  const { currentUser, updateCurrentUser } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [formData, setFormData] = useState(() => buildFormState(currentUser));
  const [profileSettings, setProfileSettings] = useState(() => buildSettingsState(currentUser));
  const [isSaving, setIsSaving] = useState(false);
  const [feedback, setFeedback] = useState('');

  useEffect(() => {
    setFormData(buildFormState(currentUser));
    setProfileSettings(buildSettingsState(currentUser));
  }, [currentUser]);

  useEffect(() => {
    setActiveTab(defaultTab);
  }, [defaultTab]);

  const initials = useMemo(() => {
    const first = formData.firstName?.charAt(0) || 'U';
    const last = formData.lastName?.charAt(0) || '';
    return `${first}${last}`.toUpperCase();
  }, [formData.firstName, formData.lastName]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSocialChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      social: {
        ...prev.social,
        [name]: value
      }
    }));
  };

  const handleSettingsChange = (e) => {
    const { name, checked } = e.target;
    setProfileSettings((prev) => ({ ...prev, [name]: checked }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setFeedback('');

    const updatedUser = await updateCurrentUser({
      first_name: formData.firstName,
      last_name: formData.lastName,
      title: formData.title,
      bio: formData.bio,
      language: formData.language,
      social: formData.social,
      settings: profileSettings
    });

    setIsSaving(false);
    setFeedback(updatedUser ? 'Cambios guardados correctamente.' : 'No se pudieron guardar los cambios.');
  };

  const Wrapper = ({ children }) => (
    embedded ? children : <div className="container mx-auto px-4 py-8">{children}</div>
  );

  return (
    <Wrapper>
      <div className={embedded ? '' : 'max-w-6xl mx-auto'}>
        {!embedded && <h1 className="text-3xl font-bold mb-8">Configuración de perfil</h1>}

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-6 border-b bg-gradient-to-r from-slate-950 via-slate-900 to-slate-800 text-white">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-white/15 border border-white/20 flex items-center justify-center text-xl font-bold">
                  {initials}
                </div>
                <div>
                  <h2 className="text-2xl font-semibold">
                    {formData.firstName || 'Tu'} {formData.lastName}
                  </h2>
                  <p className="text-sm text-slate-300">{currentUser?.email || 'usuario@educabolivia.com'}</p>
                </div>
              </div>
              <div className="text-sm text-slate-300">
                Perfil del estudiante
              </div>
            </div>
          </div>

          <div className="flex flex-wrap border-b bg-slate-50">
            <button
              className={`px-5 py-3 text-sm font-medium ${activeTab === 'perfil' ? 'text-slate-950 border-b-2 border-slate-950 bg-white' : 'text-slate-500'}`}
              onClick={() => setActiveTab('perfil')}
            >
              Perfil
            </button>
            <button
              className={`px-5 py-3 text-sm font-medium ${activeTab === 'imagen' ? 'text-slate-950 border-b-2 border-slate-950 bg-white' : 'text-slate-500'}`}
              onClick={() => setActiveTab('imagen')}
            >
              Imagen
            </button>
            <button
              className={`px-5 py-3 text-sm font-medium ${activeTab === 'configuracion' ? 'text-slate-950 border-b-2 border-slate-950 bg-white' : 'text-slate-500'}`}
              onClick={() => setActiveTab('configuracion')}
            >
              Privacidad
            </button>
          </div>

          <div className="p-6 md:p-8">
            {feedback && (
              <div className={`mb-6 rounded-xl px-4 py-3 text-sm ${feedback.includes('correctamente') ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                {feedback}
              </div>
            )}

            {activeTab === 'perfil' && (
              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                  <div>
                    <h3 className="text-xl font-semibold mb-4">Información personal</h3>

                    <div className="mb-4">
                      <label className="block text-gray-700 mb-2">Nombre</label>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-900"
                      />
                    </div>

                    <div className="mb-4">
                      <label className="block text-gray-700 mb-2">Apellido</label>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-900"
                      />
                    </div>

                    <div className="mb-4">
                      <label className="block text-gray-700 mb-2">Título profesional</label>
                      <input
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-900"
                        placeholder="Ej: Estudiante de Ingeniería"
                      />
                    </div>

                    <div className="mb-4">
                      <label className="block text-gray-700 mb-2">Biografía</label>
                      <textarea
                        name="bio"
                        value={formData.bio}
                        onChange={handleInputChange}
                        rows="5"
                        className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-900"
                        placeholder="Cuéntanos sobre ti..."
                      />
                    </div>

                    <div className="mb-4">
                      <label className="block text-gray-700 mb-2">Idioma principal</label>
                      <select
                        name="language"
                        value={formData.language}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-900"
                      >
                        <option value="Español">Español</option>
                        <option value="Inglés">Inglés</option>
                        <option value="Portugués">Portugués</option>
                        <option value="Francés">Francés</option>
                        <option value="Alemán">Alemán</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold mb-4">Redes y presencia</h3>

                    <div className="mb-4">
                      <label className="block text-gray-700 mb-2">Facebook</label>
                      <input
                        type="url"
                        name="facebook"
                        value={formData.social.facebook}
                        onChange={handleSocialChange}
                        className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-900"
                        placeholder="https://facebook.com/tuperfil"
                      />
                    </div>

                    <div className="mb-4">
                      <label className="block text-gray-700 mb-2">Instagram</label>
                      <input
                        type="url"
                        name="instagram"
                        value={formData.social.instagram}
                        onChange={handleSocialChange}
                        className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-900"
                        placeholder="https://instagram.com/tuperfil"
                      />
                    </div>

                    <div className="mb-4">
                      <label className="block text-gray-700 mb-2">LinkedIn</label>
                      <input
                        type="url"
                        name="linkedin"
                        value={formData.social.linkedin}
                        onChange={handleSocialChange}
                        className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-900"
                        placeholder="https://linkedin.com/in/tuperfil"
                      />
                    </div>

                    <div className="mb-4">
                      <label className="block text-gray-700 mb-2">TikTok</label>
                      <input
                        type="url"
                        name="tiktok"
                        value={formData.social.tiktok}
                        onChange={handleSocialChange}
                        className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-900"
                        placeholder="https://tiktok.com/@tuperfil"
                      />
                    </div>

                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 mt-6">
                      <p className="text-sm text-slate-600">
                        Tu perfil se usa en la experiencia del estudiante para personalizar panel, aprendizaje y visibilidad.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-8">
                  <button
                    type="submit"
                    disabled={isSaving}
                    className="px-6 py-3 bg-slate-950 text-white rounded-xl hover:bg-slate-800 disabled:opacity-60"
                  >
                    {isSaving ? 'Guardando...' : 'Guardar cambios'}
                  </button>
                </div>
              </form>
            )}

            {activeTab === 'imagen' && (
              <div>
                <h3 className="text-xl font-semibold mb-4">Imagen de perfil</h3>
                <div className="grid grid-cols-1 lg:grid-cols-[260px,1fr] gap-8">
                  <div className="rounded-2xl border border-dashed border-slate-300 p-6 text-center bg-slate-50">
                    <div className="w-28 h-28 rounded-full mx-auto bg-slate-900 text-white flex items-center justify-center text-3xl font-bold">
                      {initials}
                    </div>
                    <p className="mt-4 text-sm text-slate-500">
                      La carga de imagen aún no está conectada al backend, pero el módulo ya está listo para integrarse.
                    </p>
                  </div>
                  <div className="rounded-2xl border border-slate-200 p-6">
                    <p className="text-slate-700 mb-4">
                      Usa una imagen cuadrada y bien iluminada para mejorar la identificación dentro del panel de aprendizaje.
                    </p>
                    <ul className="text-sm text-slate-500 space-y-2">
                      <li>Mínimo recomendado: 200x200 px</li>
                      <li>Formato recomendado: JPG o PNG</li>
                      <li>La integración de subida puede conectarse después a tu módulo actual de imágenes</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'configuracion' && (
              <form onSubmit={handleSubmit}>
                <h3 className="text-xl font-semibold mb-4">Configuración y privacidad</h3>
                <div className="space-y-4">
                  <div className="flex items-start rounded-2xl border border-slate-200 p-4">
                    <input
                      id="showProfile"
                      name="showProfile"
                      type="checkbox"
                      checked={profileSettings.showProfile}
                      onChange={handleSettingsChange}
                      className="mt-1 h-4 w-4 text-slate-900 border-gray-300 rounded"
                    />
                    <div className="ml-3 text-sm">
                      <label htmlFor="showProfile" className="font-medium text-gray-700">
                        Mostrar tu perfil a los usuarios autenticados
                      </label>
                      <p className="text-gray-500">
                        Si está desactivado, solo tú podrás ver tu perfil desde la app.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start rounded-2xl border border-slate-200 p-4">
                    <input
                      id="showCourses"
                      name="showCourses"
                      type="checkbox"
                      checked={profileSettings.showCourses}
                      onChange={handleSettingsChange}
                      className="mt-1 h-4 w-4 text-slate-900 border-gray-300 rounded"
                    />
                    <div className="ml-3 text-sm">
                      <label htmlFor="showCourses" className="font-medium text-gray-700">
                        Mostrar los cursos que estás tomando
                      </label>
                      <p className="text-gray-500">
                        Esto permite que la experiencia social del alumno refleje lo que estás aprendiendo.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-8">
                  <button
                    type="submit"
                    disabled={isSaving}
                    className="px-6 py-3 bg-slate-950 text-white rounded-xl hover:bg-slate-800 disabled:opacity-60"
                  >
                    {isSaving ? 'Guardando...' : 'Guardar configuración'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </Wrapper>
  );
};

export default StudentProfile;
